import React from 'react';
import { Card } from 'primereact/card';
import { Message } from 'primereact/message';
import { Button } from 'primereact/button';
import { Chip } from 'primereact/chip';
import { toast } from 'react-toastify';
import { SmartHeader, SmartField, NoRecordsCard } from './_SmartFieldRenderer';

const Section = ({ icon, title, children }) => (
    <div className="pt-3 pb-3 border-bottom-1 surface-border">
        <div className="flex align-items-center gap-2 mb-3">
            <i className={`pi ${icon} text-primary`} aria-hidden="true" />
            <span className="text-sm font-semibold text-900 uppercase">{title}</span>
        </div>
        <div className="grid">{children}</div>
    </div>
);

const formatGender = (g) => (g === 'M' ? 'Male' : g === 'F' ? 'Female' : g || 'N/A');

const DrivingLicenceOutputComponent = ({
    reportData,
    onDownloadPDF,
    serviceDetails,
    actualServiceDetails,
    hideCredits,
    isSharedView,
}) => {
    const shouldHideCredits = hideCredits || isSharedView;

    let dlResponse = null;
    let errorMessage = null;

    const parsedData =
        typeof reportData === 'string'
            ? (() => {
                  try { return JSON.parse(reportData); } catch { return reportData; }
              })()
            : reportData;

    const pdfPasswordHint =
        parsedData?.pdf_password_hint ||
        reportData?.pdf_password_hint ||
        parsedData?.data?.pdf_password_hint ||
        reportData?.data?.pdf_password_hint ||
        null;

    if (reportData?.success === false) {
        errorMessage = reportData.message || 'Driving licence verification failed';
        dlResponse = reportData.response || reportData.data || {};
    } else if (reportData?.data?.response?.holderDetails) dlResponse = reportData.data.response;
    else if (reportData?.data?.licence_details?.holderDetails) dlResponse = reportData.data.licence_details;
    else if (reportData?.response?.holderDetails) dlResponse = reportData.response;
    else if (reportData?.holderDetails) dlResponse = reportData;
    else if (reportData?.data?.holderDetails) dlResponse = reportData.data;
    else dlResponse = reportData?.data || reportData?.response || reportData;

    const licenceDetails = dlResponse?.holderDetails || {};
    const covDetails = dlResponse?.covDetails || [];
    const validity = dlResponse?.validity || {};

    if (dlResponse?.error || dlResponse?.statusCode === 'error') {
        errorMessage = dlResponse?.message || dlResponse?.error || 'Driving licence verification failed';
    }

    if (errorMessage && (!dlResponse || Object.keys(licenceDetails).length === 0)) {
        return (
            <Card>
                <div className="text-center mb-3">
                    <i className="pi pi-times-circle text-5xl text-red-500 block mb-3" aria-hidden="true" />
                    <h3 className="text-lg font-semibold text-900 m-0">Driving Licence Verification Failed</h3>
                </div>
                <Message severity="error" text={errorMessage} className="w-full mb-3" />
                <p className="text-700 text-sm m-0 mb-3">Unable to verify driving licence using Tartan verification service.</p>
                <div className="surface-100 border-round p-3 mb-3">
                    <h5 className="text-900 text-sm font-semibold m-0 mb-2">Service Information</h5>
                    <p className="text-600 text-sm m-0 mb-1"><strong>Provider:</strong> Tartan Data Services</p>
                    <p className="text-600 text-sm m-0 mb-1"><strong>Service Type:</strong> Basic Driving Licence Verification</p>
                    <p className="text-600 text-sm m-0"><strong>Data Source:</strong> Transport Department Records</p>
                </div>
                <p className="text-500 text-sm m-0 mb-1">Common reasons for verification failure:</p>
                <ul className="text-500 text-sm m-0 pl-4">
                    <li>Invalid driving licence number format</li>
                    <li>Date of birth mismatch with transport records</li>
                    <li>Licence not found in Tartan database</li>
                    <li>Expired or suspended licence</li>
                    <li>Tartan service temporarily unavailable</li>
                    <li>Insufficient credits in your wallet</li>
                </ul>
                <div className="surface-200 border-round p-2 mt-3">
                    <p className="text-700 text-sm m-0">
                        <i className="pi pi-info-circle mr-2" aria-hidden="true" />
                        Tip: Try using Driving Licence V2 (GridLines) for enhanced verification coverage.
                    </p>
                </div>
            </Card>
        );
    }

    if (!licenceDetails || Object.keys(licenceDetails).length === 0) {
        return <NoRecordsCard subtitle="No driving licence details found." />;
    }

    const remainingBalance = actualServiceDetails?.available_credits || serviceDetails?.available_credits;

    const handleDownload = () => {
        if (pdfPasswordHint) {
            toast.info(pdfPasswordHint, { position: 'top-center', autoClose: 15000 });
        }
        const pdfUrl = reportData?.pdf_url || reportData?.data?.pdf_url;
        if (pdfUrl) {
            window.open(pdfUrl, '_blank', 'noopener,noreferrer');
        } else if (onDownloadPDF) {
            onDownloadPDF();
        }
    };

    return (
        <div>
            <SmartHeader title="Driving Licence Verification Report" icon="pi-id-card" remainingBalance={remainingBalance} shouldHideCredits={shouldHideCredits} />

            {errorMessage && (
                <div className="mb-3">
                    <Message severity="error" text={errorMessage} className="w-full" />
                </div>
            )}

            <Card>
                <Section icon="pi-user" title="Personal Information">
                    <SmartField name="full_name" value={licenceDetails["Holder's Name"]} />
                    <SmartField name="father_name" value={licenceDetails["Father's Name"]} />
                    <SmartField name="date_of_birth" value={licenceDetails['Date of Birth']} />
                    <SmartField name="gender" value={formatGender(licenceDetails.Gender)} />
                    {licenceDetails['Blood Group'] && <SmartField name="blood_group" value={licenceDetails['Blood Group']} />}
                    {licenceDetails.Citizenship && <SmartField name="citizenship" value={licenceDetails.Citizenship} />}
                </Section>

                <Section icon="pi-id-card" title="License Information">
                    <SmartField name="issue_date" value={licenceDetails.DOI} />
                    <SmartField name="expiry_date" value={licenceDetails['Date of Expiry']} />
                    <SmartField name="state" value={licenceDetails.State} />
                    <SmartField name="last_transaction" value={licenceDetails['Last Transaction at']} />
                    {validity['Non Transport'] && (
                        <SmartField
                            name="non_transport_validity"
                            value={`${validity['Non Transport'].From || 'N/A'} to ${validity['Non Transport'].To || 'N/A'}`}
                        />
                    )}
                    {validity.Transport && (
                        <SmartField
                            name="transport_validity"
                            value={`${validity.Transport.From || 'N/A'} to ${validity.Transport.To || 'N/A'}`}
                        />
                    )}
                </Section>

                {(licenceDetails.Address || licenceDetails.Location || licenceDetails.City || licenceDetails['Zip Code']) && (
                    <Section icon="pi-home" title="Address Information">
                        {licenceDetails.Address && (
                            <div className="col-12 flex flex-column gap-1">
                                <span className="text-500 text-xs font-medium">Complete Address</span>
                                <span className="text-900 text-sm font-semibold" style={{ wordBreak: 'break-word' }}>
                                    {licenceDetails.Address}
                                </span>
                            </div>
                        )}
                        {licenceDetails.Location && <SmartField name="location" value={licenceDetails.Location} />}
                        {licenceDetails.City && <SmartField name="city" value={licenceDetails.City} />}
                        {licenceDetails['Zip Code'] && <SmartField name="pin_code" value={licenceDetails['Zip Code']} />}
                    </Section>
                )}

                <Section icon="pi-car" title="Vehicle Classes (COV)">
                    <div className="col-12 flex flex-column gap-1">
                        <span className="text-500 text-xs font-medium">Authorized Vehicle Classes</span>
                        <div className="flex flex-wrap gap-2 mt-1">
                            {covDetails && covDetails.length > 0 ? (
                                covDetails.map((cov, index) => (
                                    <Chip key={index} label={cov['Class of Vehicle'] || 'N/A'} />
                                ))
                            ) : (
                                <span className="text-500 text-sm">No vehicle classes available</span>
                            )}
                        </div>
                    </div>
                </Section>

                <div className="text-center pt-3">
                    <Button label="Download Driving Licence Report" icon="pi pi-download" onClick={handleDownload} size="small" />
                    {pdfPasswordHint && (
                        <div className="mt-2">
                            <Message severity="info" text={pdfPasswordHint} />
                        </div>
                    )}
                </div>
            </Card>
        </div>
    );
};

export default DrivingLicenceOutputComponent;
